/**
 * The tile canvas: absolute positioning, drag to move, corner to resize.
 *
 * Tiles are placed at computed pixel offsets rather than left to a CSS grid,
 * because uPlot is handed an explicit pixel size - the layout has to be a number
 * this code knows, not one the browser settles on afterwards. All of that
 * arithmetic lives in `layout.ts`, which is pure; this component only turns
 * pointer events into calls on it.
 *
 * A drag previews against a local copy and commits once on release. Writing
 * every intermediate frame through the store would persist to storage sixty
 * times a second and re-render every tile with it.
 *
 * **The dragged tile follows the pointer; a placeholder shows where it lands.**
 * Drawing the dragged tile at its snapped slot made it look stuck whenever the
 * layout sent it back - which, with tiles floating up, is every downward move
 * into empty space. Pointer and landing spot are two different things, and
 * both are shown.
 */

import { html, nothing, type TemplateResult } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { LightElement } from '../../lit-base.ts';
import {
  deltaToGrid,
  gridHeightPx,
  layoutAfterMove,
  movedPos,
  resizedPos,
  tileRect,
  type PixelRect,
} from './layout.ts';
import {
  GRID_GAP,
  ROW_HEIGHT,
  minTileH,
  setTilePositions,
  type DashboardTile,
  type TilePos,
} from './dashboard-state.ts';
import type { SharedContext } from './tile-spec.ts';
import './dashboard-tile.ts';
import './dashboard-slicer-tile.ts';
import './tile-tip.ts';

interface DragState {
  id: string;
  mode: 'move' | 'resize';
  startX: number;
  startY: number;
  base: TilePos;
  /** Raw pointer travel in px, so a moved tile can follow the pointer exactly. */
  dxPx: number;
  dyPx: number;
  /** The grid slot the pointer is over - before collisions and floating. */
  target: TilePos;
  /** Preview layout for the whole tab, recomputed as the pointer moves. */
  preview: Map<string, TilePos>;
}

@customElement('dashboard-grid')
export class DashboardGridEl extends LightElement {
  @property({ attribute: false }) tiles: DashboardTile[] = [];
  @property({ attribute: false }) shared!: SharedContext;
  @property({ type: Boolean }) editing = false;
  /** Tiles rise into free space above them - the dashboard's `floatTiles`. */
  @property({ type: Boolean }) float = true;
  /** Cursor-sync key shared by every tile, or null when the crosshair is not shared. */
  @property({ attribute: false }) syncKey: string | null = null;
  /** Every tile the crosshair reaches shows its tooltip too. */
  @property({ type: Boolean }) sharedTooltip = false;

  @state() private hostWidth = 0;
  @state() private drag: DragState | null = null;

  private ro: ResizeObserver | null = null;

  override firstUpdated(): void {
    // Measured on the canvas, not the scroll container: the container's width
    // changes by a scrollbar's width the moment the content gets tall enough,
    // which would shift every tile sideways as the dashboard grew.
    const host = this.querySelector<HTMLElement>('.dash-grid-canvas');
    if (!host) return;
    this.hostWidth = host.clientWidth;
    this.ro = new ResizeObserver(() => {
      // Zero while another section is open (display: none). Keep the last
      // width: laying out for the fallback and back made every tile slide and
      // redraw frame by frame on the way back.
      if (host.clientWidth) this.hostWidth = host.clientWidth;
    });
    this.ro.observe(host);
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    this.ro?.disconnect();
    this.ro = null;
    this.endDrag();
  }

  /* ── Pointer handling ─────────────────────────────────── */

  private onPointerDown(e: PointerEvent, tile: DashboardTile): void {
    if (!this.editing) return;
    const el = e.target as HTMLElement;
    // Buttons in the header sit inside the drag handle; a click on one is a
    // click, not the start of a drag.
    if (el.closest('button')) return;
    const resize = !!el.closest('[data-resize-handle]');
    const move = !!el.closest('[data-drag-handle]');
    if (!resize && !move) return;

    e.preventDefault();
    (e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId);
    this.drag = {
      id: tile.id,
      mode: resize ? 'resize' : 'move',
      startX: e.clientX,
      startY: e.clientY,
      base: { ...tile.pos },
      dxPx: 0,
      dyPx: 0,
      target: { ...tile.pos },
      preview: new Map(this.tiles.map((t) => [t.id, { ...t.pos }])),
    };
    window.addEventListener('pointermove', this.onPointerMove);
    window.addEventListener('pointerup', this.onPointerUp);
  }

  private onPointerMove = (e: PointerEvent): void => {
    const drag = this.drag;
    if (!drag || !this.hostWidth) return;
    drag.dxPx = e.clientX - drag.startX;
    drag.dyPx = e.clientY - drag.startY;
    // The dragged tile follows the pointer every frame, snapped or not.
    if (drag.mode === 'move') this.requestUpdate();

    const { dx, dy } = deltaToGrid(drag.dxPx, drag.dyPx, this.hostWidth);
    const next =
      drag.mode === 'move'
        ? movedPos(drag.base, dx, dy)
        : resizedPos(drag.base, dx, dy, minTileH(this.tiles.find((t) => t.id === drag.id) ?? { kind: 'chart' }));
    if (samePos(next, drag.target)) return;
    drag.target = next;

    // Recompute the whole preview from the stored layout each frame, so an
    // earlier frame's displacement never compounds into the next one.
    const proposed = this.tiles.map((t) => (t.id === drag.id ? { ...t, pos: next } : t));
    const resolved = layoutAfterMove(proposed, drag.id, drag.base, this.float);
    drag.preview = new Map(resolved.map((r) => [r.id, r.pos]));
    this.requestUpdate();
  };

  private onPointerUp = (): void => {
    const drag = this.drag;
    if (drag) {
      const entries = [...drag.preview.entries()].map(([id, pos]) => ({ id, pos }));
      const changed = entries.filter((entry) => {
        const before = this.tiles.find((t) => t.id === entry.id);
        return before && !samePos(before.pos, entry.pos);
      });
      if (changed.length) setTilePositions(entries);
    }
    this.endDrag();
  };

  private endDrag(): void {
    window.removeEventListener('pointermove', this.onPointerMove);
    window.removeEventListener('pointerup', this.onPointerUp);
    this.drag = null;
  }

  /* ── Render ───────────────────────────────────────────── */

  private posOf(tile: DashboardTile): TilePos {
    return this.drag?.preview.get(tile.id) ?? tile.pos;
  }

  /**
   * Where a cell is drawn. The tile being moved sits under the pointer - its
   * start rect plus the raw travel, kept inside the canvas - rather than at the
   * slot it will land in, which the placeholder shows instead.
   */
  private cellRect(tile: DashboardTile, width: number): PixelRect {
    const drag = this.drag;
    if (drag?.mode === 'move' && drag.id === tile.id) {
      const start = tileRect(drag.base, width);
      return {
        ...start,
        left: Math.max(0, Math.min(width - start.width, start.left + drag.dxPx)),
        top: Math.max(0, start.top + drag.dyPx),
      };
    }
    return tileRect(this.posOf(tile), width);
  }

  private renderPlaceholder(width: number): TemplateResult | typeof nothing {
    const drag = this.drag;
    const pos = drag?.mode === 'move' ? drag.preview.get(drag.id) : undefined;
    if (!pos) return nothing;
    const r = tileRect(pos, width);
    return html`<div
      class="dash-grid-placeholder"
      style="left:${r.left}px;top:${r.top}px;width:${r.width}px;height:${r.height}px"
    ></div>`;
  }

  override render(): TemplateResult {
    const layoutTiles = this.tiles.map((t) => ({ ...t, pos: this.posOf(t) }));
    const width = this.hostWidth || 1200;
    // Drop room belongs to an active drag. Keeping it under an idle layout
    // made charts that fit the viewport scroll through two invisible rows.
    const dragged = this.drag ? this.tiles.find((t) => t.id === this.drag!.id) : undefined;
    const draggedBottom = dragged ? this.cellRect(dragged, width) : null;
    const height = Math.max(
      gridHeightPx(layoutTiles),
      draggedBottom ? draggedBottom.top + draggedBottom.height : 0,
    ) + (this.drag ? 2 * (ROW_HEIGHT + GRID_GAP) : 0);

    return html`
      <div class="dash-grid ${this.editing ? 'is-editing' : ''} ${this.drag ? 'is-dragging' : ''}">
        <div class="dash-grid-canvas" style="height:${height}px">
          ${this.renderPlaceholder(width)}
          ${this.tiles.map((tile) => {
            const rect = this.cellRect(tile, width);
            const dragging = this.drag?.id === tile.id;
            return html`
              <div
                class="dash-grid-cell ${dragging ? 'is-active' : ''}"
                data-tile-id=${tile.id}
                style="left:${rect.left}px;top:${rect.top}px;width:${rect.width}px;height:${rect.height}px"
                @pointerdown=${(e: PointerEvent) => this.onPointerDown(e, tile)}
              >
                ${tile.kind === 'slicer'
                  ? html`<dashboard-slicer-tile
                      .tile=${tile}
                      .shared=${this.shared}
                      ?editing=${this.editing}
                    ></dashboard-slicer-tile>`
                  : html`<dashboard-tile
                      .tile=${tile}
                      .shared=${this.shared}
                      .syncKey=${this.syncKey}
                      ?sharedTooltip=${this.sharedTooltip}
                      ?editing=${this.editing}
                    ></dashboard-tile>`}
              </div>
            `;
          })}
        </div>
        ${this.tiles.length === 0
          ? html`<div class="dash-grid-empty">
              <p>No tiles on this tab yet.</p>
              <p class="dash-grid-empty-hint">
                Use <strong>Add chart</strong> to build one here.
              </p>
            </div>`
          : nothing}
      </div>
    `;
  }
}

function samePos(a: TilePos, b: TilePos | undefined): boolean {
  return !!b && a.x === b.x && a.y === b.y && a.w === b.w && a.h === b.h;
}
